import { createAction, PayloadAction } from '@reduxjs/toolkit'
import isUndefined from 'lodash/isUndefined'
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { postRotatedImages } from '../../../api/projects-api'
import { DocumentChunk } from '../../../models/documentChunk'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Image, Select, Workspace } from '../../lib/toolBoxes/2D'
import { setAutoScalePageView } from '../../slices/autoScale'
import {
    initializeProjectDocumentsFromChunks,
    selectActiveDocumentChunk,
    selectDocumentChunks,
    updateSingleDocumentChunk,
} from '../../slices/documents'
import { setImageRotationInProgress } from '../../slices/loading'

export const rotateImageLocalAction = createAction<{ degrees: number }>('rotateImageLocal')
export const rotateImageRemoteAction = createAction<{ degrees: number } | undefined>('rotateImageRemote')

export interface RotateImageResponse {
    message: string
    updatedImagesData: DocumentChunk[]
}

export function* handleRotateImageLocally(action: PayloadAction<{ degrees: number }>) {
    const manager: PaperManager = yield call(managers.get2DManager)

    if (!manager) return

    const imageTool: Image = yield call(manager.getTool, Image.NAME)

    yield call(imageTool.rotateImage, action.payload.degrees)
}

export function* handleRotateImageRemote(action: PayloadAction<{ degrees: number } | undefined>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [imageTool, workspaceTool]: [Image, Workspace] = yield call(manager.getTools, [Image.NAME, Workspace.NAME])

    try {
        const currentRotation = action.payload?.degrees || imageTool.getCurrentRotation()

        if (currentRotation !== 0) {
            yield put(setImageRotationInProgress(true))

            const currentDocumentChunk: DocumentChunk = yield select(selectActiveDocumentChunk)

            if (currentDocumentChunk) {
                const rotateResponse: RotateImageResponse = yield call(postRotatedImages, [
                    { ...currentDocumentChunk, rotateDegree: currentRotation },
                ])

                const updatedChunk = rotateResponse.updatedImagesData[0]

                // Rotate degree doesnt matter in the store but it is included as part of the response payload
                if (!isUndefined(updatedChunk['rotateDegree'])) {
                    delete updatedChunk['rotateDegree']
                    updatedChunk.project_document.thumbnail = updatedChunk['thumbnailSrc']
                }
                const documentChunks: DocumentChunk[] = yield select(selectDocumentChunks)

                // Update the src URLs in the store with the new images
                yield put(updateSingleDocumentChunk(updatedChunk))
                yield put(initializeProjectDocumentsFromChunks([updatedChunk, ...documentChunks]))

                /** When the image is rotated, paper.js also rotates the cartesian plane so we need to
                clear the workspace and re-insert the now-rotated image so that the coordinates are correct
                for any regions or 2DMS classifications that are drawn after rotating. */
                yield call(workspaceTool.clear)

                // the AutoScale result was measured on the unrotated page and is re-run for the rotated one, so drop
                // it (and the dimension overlay that just went with the workspace) instead of showing misplaced lines
                yield put(setAutoScalePageView(null))
                yield call(imageTool.insertImage, updatedChunk.src)

                yield call(imageTool.normalizeRotation)
                yield put(setImageRotationInProgress(false))
            } else {
                throw new Error('Error: No active document chunk')
            }
        }
    } catch (error) {
        // Undo the local rotation if the request was unsuccessful
        yield call(imageTool.undoRotate)
        yield call(console.error, error)
        yield put(setImageRotationInProgress(false))
    }

    yield call(manager.useTool, Select.NAME)
}

export function* watchForRotateImageLocal() {
    yield takeEvery(rotateImageLocalAction.type, handleRotateImageLocally)
}

export function* watchForRotateImageRemote() {
    yield takeLatest(rotateImageRemoteAction.type, handleRotateImageRemote)
}
